import styles from './Carousel.scss';

import cx from 'classnames';
import { ReferencedEntry } from 'contentful';
import debounce from 'lodash/debounce';
import React from 'react';
import Slider from 'react-slick';

import { CarouselAssembly, CarouselType } from 'src/api/contentful/contentTypes.cloverdotcom';

import { componentKey, getField } from 'src/utils/EntryUtil';

import { Item as CarouselProductItem } from 'src/components/CarouselModule/ProductItem/ProductItem';
import { withBrowser } from '../Responsive/WithBrowser';
import { Heading } from './CarouselHeading/CarouselHeading';
import { Item as CarouselFeatureItem } from './FeatureItem/FeatureItem';
import { Item as CarouselHeroItem } from './HeroItem/HeroItem';
import { computeSliderProps } from './SliderSettingFunctions';
import { Item as CarouselSquareItem } from './SquareItem/SquareItem';

export interface ICarouselModule extends React.AllHTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<CarouselAssembly>;
  isMobile: boolean;
  isDesktop: boolean;
}

export class CarouselModule extends React.Component<ICarouselModule> {

  get carouselType() {
    return getField(this.props.entry, 'carouselType');
  }

  get isTypeProduct() {
    return this.carouselType === CarouselType.PRODUCT;
  }

  get isTypeFeature() {
    return this.carouselType === CarouselType.FEATURE;
  }

  get isTypeSquare() {
    return this.carouselType === CarouselType.SQUARE;
  }

  get isTypeHero() {
    return this.carouselType === CarouselType.HERO;
  }

  get renderHeadingAboveSlider() {
    return (this.isTypeSquare || this.isTypeProduct || this.isTypeFeature && this.props.isMobile);
  }

  get renderHeadingInsideSlider() {
    return (this.isTypeFeature && !this.props.isMobile);
  }

  sliderDivEl = React.createRef<HTMLDivElement>();
  firstItemEl = React.createRef<HTMLAnchorElement>();

  // on screen resize, determine new slidesToShow, slidesToScroll, and whether or not to display arrows
  readonly onResize = debounce(() => {
    const { entry } = this.props;

    if (!entry || !entry.fields || !entry.fields.items) return;

    this.forceUpdate();
  });

  componentDidMount() {
    if (this.isTypeFeature || this.isTypeProduct) {
      window.addEventListener('resize', this.onResize);

      // hacky fix for rendering silder correctly on first load
      window.requestAnimationFrame(this.onResize);
    }
  }

  componentWillUnmount() {
    if (this.isTypeFeature || this.isTypeProduct) {
      window.removeEventListener('resize', this.onResize);
    }
  }

  render() {
    const { entry, style, isMobile, isDesktop, ...props } = this.props; // Ignoring the styles that react-slick passes down

    if (!entry || !entry.fields) return null;

    const { items = [], backgroundColor } = entry.fields;

    if (!this.carouselType) {
      return null;
    }
    return (
      <section data-test={this.isTypeFeature ? 'page-carousel' : null} className={styles.carouselWrapper} {...props}>
        <div className={styles.carousel} ref={this.sliderDivEl}>
          { this.renderHeadingAboveSlider &&
            <Heading entry={entry} carouselType={this.carouselType} />}
          <Slider
            {...computeSliderProps(this.carouselType, items.length, this.sliderDivEl, this.firstItemEl, isMobile, isDesktop)}
            className={cx(styles.carouselSlider, { [styles.productCarouselSlider]: this.carouselType === CarouselType.PRODUCT })}
          >
            {this.renderHeadingInsideSlider &&
              <Heading entry={entry} carouselType={this.carouselType} />}
            {items.map((item, index) => {
              const baseProps = { key: componentKey(item, index), entry: item };
              switch (this.carouselType) {
                case CarouselType.PRODUCT:
                  return (
                    <CarouselProductItem
                      preload="none"
                      style={{ backgroundColor }}
                      {...baseProps}
                    />
                  );
                case CarouselType.FEATURE:
                  return (
                    <CarouselFeatureItem
                      ref={index === 0 && this.firstItemEl || undefined}
                      style={{ backgroundColor }}
                      {...baseProps}
                    />
                  );
                case CarouselType.SQUARE:
                  return (
                    <CarouselSquareItem
                      preload="none"
                      style={{ backgroundColor }}
                      {...baseProps}
                    />
                  );
                case CarouselType.HERO:
                default:
                    return (
                      <CarouselHeroItem
                        autoPlay={getField(entry, 'autoPlay')}
                        style={{ backgroundColor }}
                        {...baseProps}
                      />
                    );
              }
            })}
          </Slider>
          {this.isTypeHero && <Heading entry={entry} carouselType={this.carouselType} />}
        </div>
      </section>
    );
  }
}

const Browsered = withBrowser(browser => ({
  isMobile: browser.is.mobile,
  isDesktop: browser.is.desktop,
}))(CarouselModule);
type Browsered = typeof Browsered;

export { Browsered as CarouselContainer };
